import {Button} from "@/components/ui/button.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import type {DungeonLight} from "@/dungeons/model/DungeonGeometry.ts";
import {DEFAULT_LIGHT_COLOR_STRENGTH, LIGHT_PRESET_LABELS, LIGHT_PRESETS, type LightPreset} from "@/dungeons/lighting/LightPresets.ts";
import LightColorControl from "@/dungeons/lighting/LightColorControl.tsx";
import LightValueSlider from "@/dungeons/lighting/LightValueSlider.tsx";
import {Trash2} from "lucide-react";
import LightAnimationControl from "@/dungeons/lighting/LightAnimationControl.tsx";

export default function LightInspector({light, onChange, onPreview, onCommitPreview, onDelete}: {
    light: DungeonLight;
    onChange: (changes: Partial<DungeonLight>) => void;
    onPreview: (changes: Partial<DungeonLight>) => void;
    onCommitPreview: (changes: Partial<DungeonLight>) => void;
    onDelete: () => void;
}) {
    const preview = (changes: Partial<DungeonLight>) => onPreview({...changes, preset: "CUSTOM"});
    const commit = (changes: Partial<DungeonLight>) => onCommitPreview({...changes, preset: "CUSTOM"});
    return <div className="space-y-3 text-sm">
        <label className="block space-y-1">Preset
            <select value={light.preset} onChange={event => {
                const preset = event.target.value as LightPreset;
                if (preset !== "CUSTOM") onChange({preset, ...LIGHT_PRESETS[preset]});
            }} className="w-full rounded-md border border-input bg-surface p-2">
                {Object.entries(LIGHT_PRESET_LABELS).map(([value, label]) =>
                    <option key={value} value={value}>{label}</option>)}
            </select>
        </label>
        <LightValueSlider label="Reichweite" value={light.rangeMeters} min={0.5} max={100} step={0.5}
            format={value => `${value.toFixed(1)} m`}
            onPreview={rangeMeters => preview({rangeMeters})}
            onCommit={rangeMeters => commit({rangeMeters})}/>
        <LightValueSlider label="Helligkeit" value={light.intensity} min={0} max={1} step={0.01}
            format={value => value.toFixed(2)}
            onPreview={intensity => preview({intensity})}
            onCommit={intensity => commit({intensity})}/>
        <LightValueSlider label="Farbstärke" value={light.colorStrength ?? DEFAULT_LIGHT_COLOR_STRENGTH}
            min={0} max={1} step={0.01} format={value => value.toFixed(2)}
            onPreview={colorStrength => preview({colorStrength})}
            onCommit={colorStrength => commit({colorStrength})}/>
        <LightColorControl color={light.color}
            onPreview={color => preview({color})} onCommit={color => commit({color})}/>
        <LightAnimationControl animation={light.animation}
            onPreview={animation => preview({animation})} onCommit={animation => commit({animation})}/>
        <label className="flex cursor-pointer items-center gap-2">
            <Checkbox checked={light.enabled} onCheckedChange={checked => onChange({enabled: checked === true})}/>
            Standardmäßig eingeschaltet
        </label>
        <Button type="button" size="sm" variant="destructive" onClick={onDelete}><Trash2/> Licht löschen</Button>
    </div>;
}
